import { createTimeline, splitText, stagger, animate, utils } from 'animejs';
import Router from '@/router/index';

export const isDisabledAnimation = () => {
	const { mode } = Router.currentRoute.query;
	return mode === 'iframe';
};

/**
 * 条件动画创建包装函数
 * 根据条件判断是否执行动画创建逻辑
 * @param {Function} resolve 创建动画的函数，条件满足时执行
 * @param {Function} [reject] 条件不满足时的处理函数（可选，预备入口）
 * @returns {*} 如果符合条件则返回动画实例，否则返回 reject 的执行结果或 null
 */
const createAnimationWhenReady = (resolve, reject) => {
	const disabledAnimation = isDisabledAnimation();
	if (disabledAnimation) {
		return reject?.();
	}

	return resolve?.();
};

// 开屏动画
/**
 * 开屏标题依次滚动两轮, 整体淡出
 * @param {*} target
 */
export const openingTitleMarqueeAndFadeOut = target => {
	if (!target) return null;

	return createAnimationWhenReady(() => {
		const { chars } = splitText(target, {
			chars: {
				wrap: 'clip',
				clone: 'bottom'
			}
		});
		utils.set(chars, { opacity: 0 }); // 初始帧

		const anim = createTimeline({
			autoplay: false
		})
			// --- 1️⃣ 标题整体淡入 ---
			.add(chars, {
				opacity: [0, 1],
				duration: 320,
				ease: 'out(3)'
			})
			// --- 2️⃣ 每个字依次滚动两轮 ---
			.add(
				chars,
				{
					y: '-100%',
					loop: 1,
					duration: 500,
					ease: 'inOut(2)'
				},
				stagger(80, { from: 'first' })
			)
			// --- 3️⃣ 标题整体淡出 ---
			.add(
				chars,
				{
					opacity: [1, 0],
					duration: 320,
					ease: 'in(3)'
				},
				'+=100'
			);

		return anim;
	});
};

/**
 * 开屏背景上下两半分开移除
 * @param {*} targetList
 */
export const openingBackgroundSlideOut = targetList => {
	if (!targetList) return null;

	return createAnimationWhenReady(() => {
		const [target1, target2] = targetList;
		utils.set(target1, { top: 0, translateY: 0 }); // 初始帧
		utils.set(target2, { bottom: 0, translateY: 0 }); // 初始帧

		const anim = createTimeline({
			autoplay: false
		})
			.label('bg-top', 0)
			.label('bg-bottom', 0)
			// --- 3️⃣ 背景上下两半分开移除 ---
			.add(
				target1,
				{
					top: 0,
					translateY: ['0%', '-100%'],
					duration: 1000
				},
				'bg-top'
			)
			.add(
				target2,
				{
					bottom: 0,
					translateY: ['0%', '100%'],
					duration: 1000
				},
				'bg-bottom'
			);

		return anim;
	});
};

// ------------------------- 分割线  -------------------------
// Banner Animation
/**
 * Banner Footer 向上淡入
 * @param {*} target
 */
export const bannerFooterSlideIn = target => {
	if (!target) return null;

	return createAnimationWhenReady(() => {
		const anim = createTimeline({
			easing: 'easeOutQuad',
			duration: 600,
			autoplay: false,
			onComplete: () => {
				console.log('Banner Footer 动画完成');
			}
		}).add(
			target,
			{
				translateY: ['100%', '0%']
			},
			stagger(0, { from: 'first' })
		);
		anim.seek(0); // 初始帧
		return anim;
	});
};

/**
 * Banner Content 向上淡入
 * @param {*} target
 */
export const bannerContentFadeIn = target => {
	if (!target) return null;

	return createAnimationWhenReady(() => {
		const anim = createTimeline({
			easing: 'easeOutQuad',
			duration: 600,
			autoplay: false,
			onComplete: () => {
				console.log('Banner Content 动画完成');
			}
		}).add(
			target,
			{
				translateY: [30, 0],
				opacity: [0, 1]
			},
			stagger(0, { from: 'first' })
		);
		anim.seek(0); // 初始帧
		return anim;
	});
};

/**
 * 左侧股票信息淡入
 * @param {*} target
 */
export const fadeInLeft = target => {
	if (!target) return null;

	return createAnimationWhenReady(() => {
		const anim = createTimeline({
			easing: 'easeOutQuad',
			duration: 600,
			autoplay: false
		}).add(target, {
			translateX: [-30, 0],
			opacity: [0, 1]
		});
		anim.seek(0); // 初始帧
		return anim;
	});
};

/**
 * 右侧股票信息淡入
 * @param {*} target
 */
export const fadeInRight = target => {
	if (!target) return null;

	return createAnimationWhenReady(() => {
		const anim = createTimeline({
			easing: 'easeOutQuad',
			duration: 600,
			autoplay: false
		}).add(target, {
			translateX: [30, 0],
			opacity: [0, 1]
		});
		anim.seek(0); // 初始帧
		return anim;
	});
};

/**
 * 批量处理数字滚动
 * PS: data-animate-number => directive.js animateNumber
 * @param {*} parentTarget 父级容器
 */
export const marqueeNumber = parentTarget => {
	if (!parentTarget) return null;

	return createAnimationWhenReady(() => {
		const numberElements = parentTarget.querySelectorAll('[data-animate-number]');
		if (numberElements.length === 0) return [];

		const animTasks = [];
		numberElements.forEach(el => {
			const targetStr = el.getAttribute('data-animate-number');
			if (!targetStr) return;

			const numMatch = targetStr.match(/^(-?\d+\.?\d*)/);
			if (!numMatch) return;

			const numStr = numMatch[1];
			const targetValue = parseFloat(numStr);
			if (isNaN(targetValue)) return;

			const suffix = targetStr.replace(/^-?\d+\.?\d*/, '') || '';

			const decimalPart = numStr.split('.')[1] || '';
			const decimals = decimalPart.length;

			const isNegative = targetValue < 0;
			const absTargetValue = Math.abs(targetValue);
			const roundPad = utils.roundPad(decimals);

			const anim = animate(el, {
				innerHTML: absTargetValue,
				modifier: val => {
					const formatted = roundPad(val);
					const sign = isNegative ? '-' : '';
					return `${sign}${formatted}${suffix}`;
				},
				autoplay: false,
				duration: 2200,
				ease: 'linear'
			});
			anim.seek(0); // 初始帧
			animTasks.push(anim);
		});
		return animTasks;
	});
};

// ------------------------- 分割线  -------------------------

/**
 * 内容向上淡入
 * @param {*} target
 */
export const contentFadeIn = target => {
	if (!target) return null;

	return createAnimationWhenReady(() => {
		const anim = createTimeline({
			easing: 'easeOutQuad',
			duration: 600,
			autoplay: false,
			onComplete: () => {
				console.log('模块动画完成');
			}
		}).add(
			target,
			{
				opacity: [0, 1],
				translateY: [30, 0]
			},
			stagger(0, { from: 'first' })
		);
		anim.seek(0); // 初始帧
		return anim;
	});
};
